import { useState, useEffect } from 'react';
import { Box, Button, Input, Textarea, Select, Option, Typography, IconButton, Stack, Sheet, Link } from '@mui/joy';
import { uploadFile } from '../../services/api';
import { generateCharacteristics, generateReviews, getStoredApiKey, setStoredApiKey } from '../../services/openrouter';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

function ProductForm({ initialData, categories, onSave, onCancel }) {
  const [form, setForm] = useState({
    id: '',
    name: '',
    price: '',
    markup: '',
    description: '',
    category_id: '',
    images: [],
    characteristics: '',
    reviews: '',
    quantity: 1,
    folder: '',
  });

  const [uploading, setUploading] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [apiKey, setApiKey] = useState(() => getStoredApiKey());
  const [generatingChars, setGeneratingChars] = useState(false);
  const [generatingReviews, setGeneratingReviews] = useState(false);

  useEffect(() => {
    if (initialData) {
      const imgs = [...(initialData.images || [])];
      if (initialData.image && !imgs.includes(initialData.image)) {
        imgs.unshift(initialData.image);
      }
      setForm({
        id: initialData.id || '',
        name: initialData.name || '',
        price: initialData.price || '',
        markup: '',
        description: initialData.description || '',
        category_id: initialData.category_id || '',
        images: imgs,
        characteristics: initialData.characteristics ? JSON.stringify(initialData.characteristics, null, 2) : '',
        reviews: initialData.reviews ? JSON.stringify(initialData.reviews, null, 2) : '',
        quantity: initialData.quantity || 1,
        folder: '',
      });
    } else {
      setForm({
        id: '',
        name: '',
        price: '',
        markup: '',
        description: '',
        category_id: '',
        images: [],
        characteristics: '',
        reviews: '',
        quantity: 1,
        folder: '',
      });
    }
  }, [initialData]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleAddImageUrl = () => {
    setForm(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const handleImageUrlChange = (idx, val) => {
    const newImages = [...form.images];
    newImages[idx] = val;
    setForm(prev => ({ ...prev, images: newImages }));
  };

  const handleRemoveImage = (idx) => {
    const newImages = form.images.filter((_, i) => i !== idx);
    setForm(prev => ({ ...prev, images: newImages }));
  };

  const handleDragStart = (idx) => (e) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    const newImages = [...form.images];
    const [moved] = newImages.splice(draggedIdx, 1);
    newImages.splice(idx, 0, moved);
    setDraggedIdx(idx);
    setForm(prev => ({ ...prev, images: newImages }));
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
  };

  const handleFileSelect = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    if (!form.folder.trim()) {
      alert('Укажите папку (название товара) перед загрузкой файлов');
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const res = await uploadFile(file, form.folder.trim());
        setForm(prev => ({ ...prev, images: [...prev.images, res.url] }));
      } catch (err) {
        alert('Ошибка загрузки ' + file.name + ': ' + (err.response?.data?.error || err.message));
      }
    }
    setUploading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedImages = form.images.map(u => u.trim()).filter(Boolean);
    let finalPrice = parseFloat(form.price) || 0;
    const markup = parseFloat(form.markup);
    if (markup && markup > 0) {
      finalPrice = Math.round(finalPrice * markup * 100) / 100;
    }

    const data = {
      name: form.name.trim(),
      price: finalPrice,
      description: form.description.trim(),
      category_id: parseInt(form.category_id) || 0,
      image: trimmedImages[0] || '',
      images: trimmedImages,
      quantity: parseInt(form.quantity) || 1,
    };

    if (form.id) {
      data.id = parseInt(form.id);
    }

    if (!data.name || !data.price || !data.category_id) {
      alert('Заполните название, цену и категорию');
      return;
    }

    if (form.characteristics.trim()) {
      try {
        data.characteristics = JSON.parse(form.characteristics);
      } catch (err) {
        alert('Характеристики должны быть валидным JSON-массивом строк');
        return;
      }
    } else {
      data.characteristics = [];
    }

    if (form.reviews.trim()) {
      try {
        data.reviews = JSON.parse(form.reviews);
      } catch (err) {
        alert('Отзывы должны быть валидным JSON-массивом');
        return;
      }
    } else {
      data.reviews = [];
    }

    onSave(data);
  };

  const categoryName = categories.find(c => c.id === Number(form.category_id))?.name || '';

  const handleGenerateCharacteristics = async () => {
    if (!form.name.trim()) {
      alert('Укажите название товара');
      return;
    }
    setGeneratingChars(true);
    try {
      const chars = await generateCharacteristics(apiKey, {
        name: form.name,
        description: form.description,
        categoryName,
      });
      updateField('characteristics', JSON.stringify(chars, null, 2));
    } catch (e) {
      alert('Ошибка генерации характеристик: ' + e.message);
    }
    setGeneratingChars(false);
  };

  const handleGenerateReviews = async () => {
    if (!form.name.trim()) {
      alert('Укажите название товара');
      return;
    }
    setGeneratingReviews(true);
    try {
      const reviews = await generateReviews(apiKey, {
        name: form.name,
        description: form.description,
        categoryName,
      });
      updateField('reviews', JSON.stringify(reviews, null, 2));
    } catch (e) {
      alert('Ошибка генерации отзывов: ' + e.message);
    }
    setGeneratingReviews(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 100, maxWidth: 140 }}>
              <Typography level="body-sm" sx={{ mb: 0.5 }}>ID {initialData ? '' : '(необяз.)'}</Typography>
              <Input
                type="number"
                value={form.id}
                onChange={(e) => updateField('id', e.target.value)}
                disabled={!!initialData}
                placeholder="Авто"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <Typography level="body-sm" sx={{ mb: 0.5 }}>Название *</Typography>
              <Input value={form.name} onChange={(e) => updateField('name', e.target.value)} fullWidth />
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography level="body-sm" sx={{ mb: 0.5 }}>Цена *</Typography>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => updateField('price', e.target.value)} />
            </Box>
            <Box sx={{ minWidth: 100 }}>
              <Typography level="body-sm" sx={{ mb: 0.5 }}>Наценка</Typography>
              <Input type="number" step="0.01" value={form.markup} onChange={(e) => updateField('markup', e.target.value)} placeholder="1.8" />
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <Typography level="body-sm" sx={{ mb: 0.5 }}>Категория *</Typography>
              <Select value={form.category_id || undefined} onChange={(e, val) => updateField('category_id', val ? Number(val) : '')} placeholder="Выберите категорию">
                {categories.map(c => (
                  <Option key={c.id} value={c.id}>{c.name}</Option>
                ))}
              </Select>
            </Box>
          </Box>

          <Box>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>Описание</Typography>
            <Textarea minRows={3} value={form.description} onChange={(e) => updateField('description', e.target.value)} />
          </Box>

          <Sheet variant="soft" sx={{ p: 2, borderRadius: 12 }}>
            <Typography level="body-sm" sx={{ mb: 1 }}>
              OpenRouter API ключ{' '}
              <Link href="https://openrouter.ai/keys" target="_blank" level="body-xs">
                (получить)
              </Link>
            </Typography>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => {
                const val = e.target.value;
                setApiKey(val);
                setStoredApiKey(val);
              }}
              placeholder="sk-or-..."
              fullWidth
            />
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
              Ключ сохраняется локально в браузере. Используется для генерации характеристик и отзывов.
            </Typography>
          </Sheet>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ minWidth: 120 }}>
              <Typography level="body-sm" sx={{ mb: 0.5 }}>Количество</Typography>
              <Input type="number" value={form.quantity} onChange={(e) => updateField('quantity', e.target.value)} />
            </Box>
          </Box>

          <Box>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Изображения <Typography level="body-xs" sx={{ color: 'neutral.500' }}>(первое — главное, перетащите для смены порядка)</Typography>
            </Typography>
            {form.images.map((url, idx) => (
              <Box
                key={idx}
                draggable
                onDragStart={handleDragStart(idx)}
                onDragOver={handleDragOver(idx)}
                onDragEnd={handleDragEnd}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  mb: 1,
                  p: 1,
                  borderRadius: 8,
                  border: '1px solid',
                  borderColor: draggedIdx === idx ? 'primary.outlinedBorder' : 'neutral.outlinedBorder',
                  bgcolor: draggedIdx === idx ? 'primary.softBg' : 'transparent',
                  cursor: 'move',
                }}
              >
                <DragIndicatorIcon sx={{ color: 'neutral.500', cursor: 'grab' }} />
                <Typography level="body-xs" sx={{
                  minWidth: 28,
                  textAlign: 'center',
                  fontWeight: idx === 0 ? 'bold' : 'normal',
                  color: idx === 0 ? 'primary.plainColor' : 'inherit',
                }}>
                  {idx === 0 ? '★' : idx + 1}
                </Typography>
                <Input
                  value={url}
                  onChange={(e) => handleImageUrlChange(idx, e.target.value)}
                  placeholder="URL изображения"
                  sx={{ flex: 1 }}
                />
                {url && (
                  <Box
                    component="img"
                    src={url}
                    alt=""
                    sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <IconButton size="sm" color="danger" onClick={() => handleRemoveImage(idx)}><DeleteIcon /></IconButton>
              </Box>
            ))}
            <Button size="sm" variant="soft" startDecorator={<AddIcon />} onClick={handleAddImageUrl}>Добавить URL</Button>
          </Box>

          <Box>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>Загрузка файлов</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
              <Box sx={{ minWidth: 200, flex: 1 }}>
                <Input
                  value={form.folder}
                  onChange={(e) => updateField('folder', e.target.value)}
                  placeholder="Папка в productsImages (название товара)"
                />
              </Box>
              <Box>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  style={{ display: 'none' }}
                  id="upload-input"
                />
                <label htmlFor="upload-input">
                  <Button size="sm" variant="outlined" component="span" startDecorator={<UploadIcon />}>Выбрать файлы</Button>
                </label>
              </Box>
            </Box>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
                  setIsDraggingFiles(true);
                }
              }}
              onDragLeave={() => setIsDraggingFiles(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDraggingFiles(false);
                if (e.dataTransfer.files && e.dataTransfer.files.length) {
                  handleFileSelect(e.dataTransfer.files);
                }
              }}
              sx={{
                border: '2px dashed',
                borderColor: isDraggingFiles ? 'primary.outlinedBorder' : 'neutral.outlinedBorder',
                bgcolor: isDraggingFiles ? 'primary.softBg' : 'background.surface',
                borderRadius: 12,
                p: 3,
                textAlign: 'center',
                transition: '0.2s',
              }}
            >
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                {isDraggingFiles ? 'Отпустите файлы для загрузки' : 'Или перетащите файлы сюда'}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography level="body-sm">Характеристики (JSON-массив строк)</Typography>
              <Button
                size="sm"
                variant="soft"
                color="primary"
                startDecorator={<AutoFixHighIcon />}
                loading={generatingChars}
                onClick={handleGenerateCharacteristics}
                disabled={!apiKey}
              >
                Сгенерировать
              </Button>
            </Box>
            <Textarea minRows={4} value={form.characteristics} onChange={(e) => updateField('characteristics', e.target.value)} placeholder={`[\n  "Производитель: ...",\n  "Max скорость: ...",\n  "Вес: ..."\n]`} />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography level="body-sm">Отзывы (JSON-массив)</Typography>
              <Button
                size="sm"
                variant="soft"
                color="primary"
                startDecorator={<AutoFixHighIcon />}
                loading={generatingReviews}
                onClick={handleGenerateReviews}
                disabled={!apiKey}
              >
                Сгенерировать
              </Button>
            </Box>
            <Textarea minRows={4} value={form.reviews} onChange={(e) => updateField('reviews', e.target.value)} placeholder={`[\n  { "id": 1, "name": "...", "comment": "...", "stars": 5 }\n]`} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
            <Button type="submit" loading={uploading}>{initialData ? 'Сохранить' : 'Добавить'}</Button>
            <Button type="button" variant="soft" color="neutral" onClick={onCancel}>Отмена</Button>
          </Box>
        </Stack>
      </Box>
  );
}

export default ProductForm;
